export const PERIOD_TYPE = {
	OVERTIME_ACTION:         'overtime_action',
	PENALTY_SHOOTOUT_ACTION: 'penalty_shootout_action',
	ACTION:                  'action',
	BREAK:                   'break',
};

export const CLOCK_START = 'clock_start';
export const CLOCK_STOP = 'clock_end';
export const CLOCK_SET = 'clock_set';

const ACTION_TIMEOUT = 'timeout';

const SOFT_TIME_CAP_MINUTES = 42;
const TIMEOUT_DURATION_SECONDS = 60; // 1 minute

const clockTagNames = new Set([CLOCK_START, CLOCK_STOP, CLOCK_SET]);
const timeoutTagNames = new Set([ACTION_TIMEOUT]);

const getPeriods = ({ event, tags }) => {
	// Ultimate frisbee is one continuous period from game_start to game_end
	const gameStartTag = tags.find(tag => tag.tag.name === 'game_start');
	const gameEndTag = tags.find(tag => tag.tag.name === 'game_end');

	const gameStart = gameStartTag?.timestamp ?? 0;
	const gameEnd = gameEndTag?.timestamp ?? Number.POSITIVE_INFINITY;

	return [{
		type:        PERIOD_TYPE.ACTION,
		start:       gameStart,
		end:         gameEnd,
		eventPeriod: event.periods?.length > 0 ? event.periods[0] : null,
	}];
};

const getPeriod = ({ position, event, tags, periods = null, additionalTimeAfterPeriodEnd = 0 }) => {
	const gamePeriods = Array.isArray(periods) ? periods : getPeriods({ event, tags });
	for (const period of gamePeriods) {
		if (period.start <= position && (period.end + additionalTimeAfterPeriodEnd) >= position) {
			return {
				...period,
				hasEnded: period.end < position,
			};
		}
	}
	return null;
};

const getGameTime = ({ position, tags }) => {
	// Find when the game started
	const gameStartTag = tags.find(tag => tag.tag.name === 'game_start');
	const gameStartPosition = gameStartTag?.timestamp ?? 0;

	// Get all timeout tags to check if we're currently in a timeout
	const timeoutTags = tags.filter(
		(eventTag) => timeoutTagNames.has(eventTag.tag.name) && eventTag.timestamp < position
	);
	timeoutTags.sort((a, b) => b.timestamp - a.timestamp); // Most recent first

	// Check if we're currently in a timeout period
	const currentTimeout = timeoutTags.find(timeout => {
		const timeoutEnd = timeout.timestamp + TIMEOUT_DURATION_SECONDS;
		return position >= timeout.timestamp && position < timeoutEnd;
	});

	// Get all relevant tags (clock tags and timeout tags) from game start to current position
	const allRelevantTags = tags.filter(
		(eventTag) =>
			(clockTagNames.has(eventTag.tag.name) || timeoutTagNames.has(eventTag.tag.name)) &&
			eventTag.timestamp >= gameStartPosition &&
			eventTag.timestamp < position
	);
	allRelevantTags.sort((a, b) => a.timestamp - b.timestamp);

	// Start with soft time cap: 42 minutes (2520 seconds)
	let gameSeconds = SOFT_TIME_CAP_MINUTES * 60;

	// Track clock state
	let lastClockStartPosition = gameStartPosition;
	let isClockRunning = true; // Clock starts running at game start

	// Process all tags chronologically
	for (const tag of allRelevantTags) {
		if (clockTagNames.has(tag.tag.name)) {
			switch (tag.tag.name) {
			case CLOCK_START:
				// Clock resumes - subtract time that passed while clock was stopped
				if (Number.isFinite(lastClockStartPosition) && isClockRunning) {
					gameSeconds -= tag.timestamp - lastClockStartPosition;
				}
				lastClockStartPosition = tag.timestamp;
				isClockRunning = true;
				break;
			case CLOCK_STOP:
				// Clock stops - subtract time that passed while clock was running
				if (Number.isFinite(lastClockStartPosition) && isClockRunning) {
					gameSeconds -= tag.timestamp - lastClockStartPosition;
				}
				lastClockStartPosition = tag.timestamp;
				isClockRunning = false;
				break;
			case CLOCK_SET:
				// Manually set clock time
				gameSeconds = Number.parseInt(tag.value);
				lastClockStartPosition = tag.timestamp;
				isClockRunning = true;
				break;
			default:
				throw new Error('Expected a clock tag.');
			}
		} else if (timeoutTagNames.has(tag.tag.name)) {
			// Timeout stops the clock for 1 minute (60 seconds)
			// First, subtract time that passed while clock was running
			if (Number.isFinite(lastClockStartPosition) && isClockRunning) {
				gameSeconds -= tag.timestamp - lastClockStartPosition;
			}
			// Clock stops - it will resume after 60 seconds
			lastClockStartPosition = tag.timestamp;
			isClockRunning = false;
		}
	}

	// Handle timeout resume: if we're past a timeout period, clock should resume
	if (!isClockRunning && timeoutTags.length > 0) {
		const mostRecentTimeout = timeoutTags[0];
		const timeoutEnd = mostRecentTimeout.timestamp + TIMEOUT_DURATION_SECONDS;

		if (position >= timeoutEnd) {
			// We're past the timeout, clock should resume
			// Don't subtract time during the timeout period (it was already stopped)
			lastClockStartPosition = timeoutEnd;
			isClockRunning = true;
		}
	}

	// If we're currently in a timeout, clock is stopped
	if (currentTimeout) {
		isClockRunning = false;
		lastClockStartPosition = currentTimeout.timestamp;
	}

	// If clock is currently running, subtract elapsed time since last start/resume
	if (isClockRunning && Number.isFinite(lastClockStartPosition)) {
		gameSeconds -= position - lastClockStartPosition;
	}

	// Clock can go negative (soft time cap passed), but we still display it
	// For display, show 0:00 if negative, but the logic can handle negative values
	const displaySeconds = Math.max(0, gameSeconds);

	return {
		minutes: Math.floor(displaySeconds / 60),
		seconds: displaySeconds % 60,
	};
};

export { getGameTime, getPeriods, getPeriod };
